import { CaretDownOutlined, CheckOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, notification, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { deviceAdminApi } from '../../../../api';
import {
	buildActionColumn,
	buildColumn,
	ColumnsProps,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getSelectedRowKeysPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	lockedValidation,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '../../../../Helper';
import { getPermissionErrorMessage, SecuredComponents, User } from '../../../../model/AccountModel';
import {
	BaseColumns,
	OperatorsColumn,
	PaginationSetting,
	ResponseObject,
	ResponseStatusCode,
	SelectedDeviceKeys,
	SelectedDevicePage,
	SelectOptions,
} from '../../../../model/CommonModel';
import {
	ButtonBuilder,
	DeviceObjectType,
	DeviceSelectedPage,
	Holidays,
	OptionsButtonBuilder,
	SelectedRowKeyPagination,
	VelocityConfigurationModel,
} from '../../../../model/DeviceAdminModel';
import { useStoreSelector } from '../../../../store';
import {
	clearVelocityConfigurationSelectionsAction,
	setHolidaysBy,
	setHolidaysByData,
	setVelocityConfigurationEditMode,
	setVelocityConfigurationFilterMode,
	setVelocitySelectedRowKeysPaginationAction,
} from '../../../../store/deviceControl/actions';
import { selectTablePaginationSetting, selectVelocityConfiguration } from '../../../../store/deviceControl/selectors';
import { ButtonDropdown, EditableCell, ModalConfirmationList, ModalWarning, SearchColumn } from '../../../common';
import { HolidayAlert } from './HolidayAlert/HolidayAlert';
import { HolidayModal } from './HolidayModal/HolidayModal';
import styles from './holidays.module.scss';

type Props = {};

type ColumnsHolidays = {
	Action: React.ReactNode;
	Name: React.ReactNode;
	T1: React.ReactNode;
	T2: React.ReactNode;
	T3: React.ReactNode;
	T4: React.ReactNode;
} & BaseColumns;

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Holiday);
const scroll: ScrollType = { x: 379, y: 312 };
const maxLength: number = 32;
const deviceObjectType: DeviceObjectType = DeviceObjectType.Holidays;

const createButtonOptions = (hasMoreThanOne: boolean, isItemOnSamePage: boolean): OptionsButtonBuilder<string> => {
	return {
		labelOrIcon: '...',
		options: [
			{
				//This only uses can view permissions since we disabled the Save button on the component itself, if it doesn't have permissions
				label: _('Edit'),
				disabled: !componentPermission.canView || hasMoreThanOne,
				id: 'holidaysTableActionDropdownEdit',
				value: 'edit',
				title: getPermissionErrorMessage(componentPermission.canView, false, hasMoreThanOne),
			},
			{
				label: _('Rename'),
				id: 'holidaysTableActionDropdownRename',
				disabled: !componentPermission.canUpdate || hasMoreThanOne || !isItemOnSamePage,
				title: getPermissionErrorMessage(componentPermission.canUpdate, false, hasMoreThanOne),
				value: 'rename',
			},
			{
				label: _('Delete'),
				id: 'holidaysTableActionDropdownDelete',
				disabled: !componentPermission.canDelete,
				title: getPermissionErrorMessage(componentPermission.canDelete),
				value: 'delete',
			},
		],
	};
};

const Holidays: React.FC<Props> = () => {
	const { holidays, isEditMode, isFilterMode, selectedRowKeysPagination }: VelocityConfigurationModel = useStoreSelector(selectVelocityConfiguration);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);

	const dispatch = useDispatch();
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const [editingKey, setEditingKey] = useState('');
	const [isItemOnSamePage, setIsItemOnSamePage] = useState<boolean>(false);
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [tableData, setTableData] = useState<ColumnsHolidays[]>([]);
	const [openModal, setOpenModal] = useState(false);
	const [holidayToEdit, setHolidayToEdit] = useState(0);
	const [selectedHolidayPage, setSelectedHolidayPage] = useState<SelectedDevicePage>(null);
	const [form] = Form.useForm();

	const redirectPage = useRef<boolean>(false);
	const isSearchPerformed = useRef<boolean>(false);

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setIsItemOnSamePage(isSamePage);
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	useEffect(() => {
		const mapRes: ColumnsHolidays[] = createData();
		setTableData(mapRes);
	}, [JSON.stringify(holidays)]);

	// * This hook is responsible for loading the previously edited command set
	useEffect(() => {
		if (selectedRowKeys.length === 1 && redirectPage.current) {
			const holidayId = Number(selectedRowKeys?.[0]);
			getSelectedHolidayPage(holidayId);
		}
	}, [redirectPage.current]);

	useEffect(() => {
		if (selectedHolidayPage) {
			setRedirectPage(false);
			setIsItemOnSamePage(true);

			const { deviceId, pageNumber } = selectedHolidayPage;

			batch(() => {
				dispatch(setVelocitySelectedRowKeysPaginationAction([{ Id: deviceId.toString(), PaginationPage: pageNumber }]));
				dispatch(setHolidaysBy({ ...tablePaginationSetting, PageNumber: pageNumber, SearchedValue: '' }));
			});

			setSelectedHolidayPage(null);
		}
	}, [JSON.stringify(selectedHolidayPage)]);

	// * This hook ensure that always return a valid page number when no values has returned
	useEffect(() => {
		if (tablePaginationSetting.TotalItems === 0 && tablePaginationSetting.PageNumber > 1) {
			const { PageNumber: currentPage } = tablePaginationSetting;
			dispatch(
				setHolidaysBy({
					...tablePaginationSetting,
					PageNumber: currentPage - 1,
				})
			);
		}
	}, [tablePaginationSetting]);

	useEffect(() => {
		return () => {
			clearSelection();
		};
	}, []);

	const getSelectedHolidayPage = async (deviceId: number) => {
		const deviceSelectedPage: DeviceSelectedPage = {
			DeviceId: deviceId,
			DeviceObjectType: deviceObjectType,
			IsMasterDoorGroup: false,
		};
		const response = await deviceAdminApi.getVelocityDeviceSelectedPage(deviceSelectedPage, { ...tablePaginationSetting, SearchedValue: '' });
		const { Entity: pageNumber } = response;

		setSelectedHolidayPage({ deviceId, pageNumber });
	};

	const clearSelection = () => {
		if (redirectPage.current) {
			return;
		}
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: deviceObjectType }));
		setSelectedRowKeys([]);
	};

	const onRemoveHolidayLocks = async (holidayID: number): Promise<void> => {
		await deviceAdminApi.unlockComponentDeviceAdmin(holidayID, SecuredComponents.Holiday);
	};

	const onDeleteHolidayRequest = async (holiday: Holidays): Promise<void> => {
		const holidayID = holiday.HolidayID;
		const response = await deviceAdminApi.deleteHoliday(holidayID);
		if (handleResponse(response)) {
			return;
		}

		if (response.Entity?.length > 0) {
			ModalWarning({
				okText: _('Ok'),
				content: <HolidayAlert data={response.Entity} />,
				title: _('HolidaysInUse'),
			});
		}

		onRemoveHolidayLocks(holidayID);
	};

	const handleOkConfirmation = async (selectedDeviceKeys: SelectedDeviceKeys[]) => {
		const selectedHolidays = selectedDeviceKeys.map(x => ({ HolidayID: x.Id, Name: x.Name } as Holidays));
		for (const holiday of selectedHolidays) {
			await onDeleteHolidayRequest(holiday);
		}
		setSelectedRowKeys([]);
		dispatch(setVelocitySelectedRowKeysPaginationAction([]));
		dispatch(setHolidaysBy(tablePaginationSetting));
	};

	const disabled: boolean = editingKey !== '';
	// const holidaysSelected: Holidays[] = holidays.filter(c => c.checked);
	const hasMoreThanOneKeySelected: boolean = selectedRowKeys.length > 1;
	const items = createButtonOptions(hasMoreThanOneKeySelected, isItemOnSamePage);

	const handleOnSearchResults = (searchedValue: string) => {
		if (redirectPage.current) {
			return;
		}
		dispatch(setHolidaysBy({ ...tablePaginationSetting, TotalItems: 0, PageNumber: 1, SearchedValue: searchedValue }));
	};

	let columns: ColumnsProps<ColumnsHolidays>[] = [
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			editable: true,
			sorter: !isEditMode ? (a, b) => a.Name.toString().localeCompare(b.Name.toString()) : undefined,
			...SearchColumn({
				maxLength,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: isEditMode,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
		},
		{
			...buildColumn('1', 'T1', '80px', 'start'),
		},
		{
			...buildColumn('2', 'T2', '80px', 'start'),
		},
		{
			...buildColumn('3', 'T3', '80px', 'start'),
		},
		{
			...buildColumn('4', 'T4', '80px', 'start'),
		},
	];
	if (isEditMode) {
		columns.unshift(buildActionColumn(disabled));
	}

	const onChangeSelection = (key: BaseColumns) => {
		const holidayID = key.key.toString();
		if (selectedRowKeys.findIndex(key => key === holidayID) === -1) {
			const cloneState: Holidays[] = holidays.map(x => (x.HolidayID.toString() === holidayID ? { ...x, checked: true } : { ...x, checked: false }));
			dispatch(setHolidaysByData(cloneState));

			const selectedKeysPagination: SelectedRowKeyPagination = {
				Id: holidayID.toString(),
				PaginationPage: tablePaginationSetting.PageNumber,
			};
			setIsItemOnSamePage(true);
			dispatch(setVelocitySelectedRowKeysPaginationAction([{ ...selectedKeysPagination }]));
			setSelectedRowKeys([holidayID]);
		}
	};

	const isEditing = (record: ColumnsHolidays) => record.key.toString() === editingKey;
	const contextMenuOptions: SelectOptions<string>[] = [...items?.options];

	const mergedColumns = columns.map(col =>
		!col.editable
			? {
					...col,
					onCell: (record: ColumnsHolidays) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
					}),
			  }
			: {
					...col,
					onCell: (record: ColumnsHolidays) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,

						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const getSelectedHolidays = async (): Promise<SelectedDeviceKeys[]> => {
		const response = await deviceAdminApi.getVelocityDevicesBySelectedKeys(deviceObjectType, selectedRowKeys as string[]);

		if (handleResponse(response)) {
			return [];
		}

		return response.Entity;
	};

	const getHoliday = async (holidayId: number): Promise<Holidays> => {
		const response = await deviceAdminApi.getHoliday(holidayId);

		if (handleResponse(response)) {
			return;
		}

		return response.Entity;
	};

	const getSelectedHoliday = async (holidayId: number): Promise<Holidays> => {
		return holidays.find(x => x.HolidayID === holidayId) ?? (await getHoliday(holidayId));
	};

	const handleActionScope = async (key: string) => {
		if (key === 'delete') {
			const selectedHolidays = await getSelectedHolidays();
			ModalConfirmationList({
				type: _('Holiday'),
				data: selectedHolidays,
				onConfirm: () => handleOkConfirmation(selectedHolidays),
			});

			return;
		}

		const selectedHolidayId = Number(selectedRowKeys?.[0]);
		const holiday: Holidays = await getSelectedHoliday(selectedHolidayId);
		const lockComponentCondition: boolean = key === 'edit';
		const isLocked: boolean = await lockedValidation(DeviceObjectType.Holidays, holiday.HolidayID, lockComponentCondition);
		if (isLocked) {
			return;
		}

		switch (key) {
			case 'rename':
				const cloneState: Holidays[] = changeStateProps(holiday.HolidayID, { editable: true });
				const isLocked = await lockedValidation(DeviceObjectType.Holidays, holiday.HolidayID);
				if (!isLocked) {
					dispatch(setVelocityConfigurationEditMode(true));
					const findKey: ColumnsHolidays = tableData.find(x => x.key === holiday.HolidayID.toString());
					edit(findKey);
					dispatch(setHolidaysByData(cloneState));
				}
				break;

			case 'edit':
				handleSetModalVisible(true, holiday.HolidayID);
				break;
		}
	};

	const changeStateProps = (deviceId: number, holiday: Partial<Holidays>): Holidays[] => {
		const cloneState: Holidays[] = [...holidays];
		const index = cloneState.findIndex(x => x.HolidayID === deviceId);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...holiday });
		}

		return cloneState;
	};

	const edit = (record: ColumnsHolidays) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.key.toString());
	};

	const resetSearchColumn = () => {
		if (isFilterMode) {
			setShouldResetSearchColumn(true);
		}
	};

	const handleOnSaveEditRow = async (deviceId: number) => {
		const isLocked: boolean = await lockedValidation(DeviceObjectType.Holidays, deviceId, false);
		if (isLocked) {
			setEditingKey('');
			const cloneState: Holidays[] = changeStateProps(deviceId, { editable: false });
			dispatch(setHolidaysByData(cloneState));
			dispatch(setVelocityConfigurationEditMode(false));
			return;
		}

		const name: string = form.getFieldValue('Name');
		const nameError: ResponseObject = await deviceAdminApi.renameDevice(DeviceObjectType.Holidays, name.trim(), deviceId, 0);
		if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
			setEditingKey('');
			dispatch(setVelocityConfigurationEditMode(false));
			dispatch(setHolidaysBy(tablePaginationSetting));
			return;
		}

		setEditingKey('');
		dispatch(setVelocityConfigurationEditMode(false));
		resetSearchColumn();
		setRedirectPage(true);
		dispatch(setHolidaysBy(tablePaginationSetting));
	};

	const handleOnCancelEditRow = (deviceId: number) => {
		const cloneState: Holidays[] = changeStateProps(deviceId, { editable: false });
		dispatch(setHolidaysByData(cloneState));
		dispatch(setVelocityConfigurationEditMode(false));
		setEditingKey('');
	};

	const getSelectedRowKeysForSelectAll = async (newSelectedRowKeys: React.Key[]): Promise<React.Key[]> => {
		const response = await deviceAdminApi.getHolidays();
		const commandSetsIds = response?.map<string>(x => x.HolidayID.toString());
		return getUniqueValuesArray(newSelectedRowKeys, commandSetsIds);
	};

	const handleChange = (selectedRowKeys: React.Key[]) => {
		const cloneState: Holidays[] = holidays.map(x => ({ ...x, checked: selectedRowKeys.findIndex(r => r === x.HolidayID) !== -1 }));
		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			selectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(selectedRowKeys);
	};

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys = await getSelectedRowKeysForSelectAll(selectedRowKeys);

		const cloneState: Holidays[] = holidays.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.HolidayID?.toString()) !== -1 }));
		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(newSelectedRowKeys);
		setIsTableLoading(false);
	};

	const handleSelectInvert = () => {
		const dataKeys = holidays.map<React.Key>(x => x.HolidayID?.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		const cloneState: Holidays[] = holidays.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.HolidayID?.toString()) !== -1,
		}));

		dispatch(setHolidaysByData(cloneState));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const setRedirectPage = (value: boolean) => {
		redirectPage.current = value;
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleSetModalVisible = (visible: boolean, holiday?: number) => {
		setHolidayToEdit(holiday);
		setOpenModal(visible);
	};

	const createActionElement = (holiday: Holidays): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (holiday.editable) {
			const holidayID = holiday.HolidayID;

			content = (
				<>
					<Button id={`holidaysTableActionDropdownSaveButton-${holidayID}`} key='save' type='primary' onClick={() => handleOnSaveEditRow(holidayID)}>
						{_('Save')}
					</Button>
					<Button id={`holidaysTableActionDropdownCancelButton-${holidayID}`} key='cancel' onClick={() => handleOnCancelEditRow(holidayID)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}

		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): ColumnsHolidays[] => {
		if (holidays.length > 0) {
			return holidays.map<ColumnsHolidays>((data: Holidays) => ({
				key: data.HolidayID.toString(),
				Action: createActionElement(data),
				Name: data.Name,
				T1: data.T1 === 1 ? <CheckOutlined /> : null,
				T2: data.T2 === 1 ? <CheckOutlined /> : null,
				T3: data.T3 === 1 ? <CheckOutlined /> : null,
				T4: data.T4 === 1 ? <CheckOutlined /> : null,
			}));
		}

		return [];
	};

	const buttonsBuilder = (button: ButtonBuilder[]): React.ReactNode =>
		button.map(x => (
			<Button id={x.id} key={x.label} onClick={x.onClick} disabled={x.disabled} title={x.title} type='primary'>
				{x.icon} {x.label}
			</Button>
		));

	const buttons: ButtonBuilder[] = [
		{
			label: _('AddHoliday'),
			icon: <PlusOutlined />,
			disabled: !componentPermission.canAdd || isEditMode,
			title: getPermissionErrorMessage(componentPermission.canAdd),
			onClick: () => handleSetModalVisible(true, undefined),
			id: 'holidaysAddHolydayButton',
		},
	];

	const rowSelection: TableRowSelection<ColumnsHolidays> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `holidaysTableCheckbox-${record.key}`,
			disabled,
			children: <label htmlFor={`holidaysTableCheckbox-${record.key}`} className={styles.srOnly}>{`${_('SelectHoliday')}`}</label>,
		}),
		selectedRowKeys,
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<OperatorsColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			dispatch(
				setHolidaysBy({ ...tablePaginationSetting, PageNumber: current, PageSize: pageSize, SortDirection: sortOrder, SortField: sortField.toString() })
			);
		}
	};

	const disabledActionButtons = disabled || selectedRowKeys.length === 0;

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<ButtonDropdown
					id={'holidaysTableActionDropdown'}
					disabled={disabledActionButtons}
					menuOptions={items.options}
					icon={<CaretDownOutlined />}
					labelIcon={items.labelOrIcon}
					onClickOption={handleActionScope}
				/>
				{buttonsBuilder(buttons)}
			</div>
			<Form form={form} component={false}>
				<Table
					id='holidayTable'
					columns={mergedColumns as ColumnsType}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					onRow={(record, rowIndex) => {
						return {
							id: `holidaysTableRow${rowIndex}`,
						};
					}}
					dataSource={tableData}
					pagination={pagination}
					scroll={scroll}
					size='small'
					className={styles.statusWidgetTable}
					rowSelection={rowSelection}
					onChange={handleOnChangeTable}
					loading={isTableLoading}
				/>
			</Form>
			{openModal && (
				<HolidayModal
					holidayId={holidayToEdit}
					onSetVisible={() => handleSetModalVisible(false)}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
				/>
			)}
		</div>
	);
};

export { Holidays };
